import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Toaster } from 'react-hot-toast';
import ScrollToTop from './components/common/ScrollToTop';

// User/Public Pages           
import Home from './pages/user/Home';
import About from './pages/user/About';
import Features from './pages/user/Features';
import Services from './pages/user/Services';
import Blog from './pages/user/Blog';
import BlogDetails from './pages/user/BlogDetails';
import Testimonials from './pages/user/Testimonials';
import AppPage from './pages/user/App';
import Contact from './pages/user/Contact';
import Store from './pages/user/Store';
import StoreDetails from './pages/user/StoreDetails';
import PrivacyPolicy from './pages/user/PrivacyPolicy';
import TermsOfService from './pages/user/TermsOfService';
import Careers from './pages/user/Careers';
import JobDetails from './pages/user/JobDetails';
import LoginPortal from './pages/user/LoginPortal';

// Service Detail Pages
import QRPaymentSolutions from './pages/user/services/QRPaymentSolutions';
import BusinessDiscoveryPlatform from './pages/user/services/BusinessDiscoveryPlatform';
import DigitalWalletSystem from './pages/user/services/DigitalWalletSystem';
import AnalyticsReporting from './pages/user/services/AnalyticsReporting';
import KYCVerification from './pages/user/services/KYCVerification';
import MobileApplications from './pages/user/services/MobileApplications';
import MultiPlatformSupport from './pages/user/services/MultiPlatformSupport';
import Support247 from './pages/user/services/Support247';

// Admin Components & Pages
import AdminLogin from './pages/admin/AdminLogin';
import ForgotPassword from './pages/admin/ForgotPassword';
import AdminLayout from './components/admin/AdminLayout';
import SuperAdminLayout from './components/admin/SuperAdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import BusinessManagement from './pages/admin/BusinessManagement';
import BusinessDetails from './pages/admin/BusinessDetails';
import CategoryManagement from './pages/admin/CategoryManagement';
import CategoryBuilder from './pages/admin/CategoryBuilder';
import DynamicFieldsManagement from './pages/admin/DynamicFieldsManagement';
import FieldGroupsManagement from './pages/admin/FieldGroupsManagement';
import UserManagement from './pages/admin/UserManagement';
import UserDetails from './pages/admin/UserDetails';
import EditUser from './pages/admin/EditUser';
import PaymentManagement from './pages/admin/PaymentManagement';
import WalletSettlementManagement from './pages/admin/WalletSettlementManagement';
import SettlementDetails from './pages/admin/SettlementDetails';
import ReportsAnalytics from './pages/admin/ReportsAnalytics';
import AppConfiguration from './pages/admin/AppConfiguration';
import SupportDisputeManagement from './pages/admin/SupportDisputeManagement';
import SupportTicketDetails from './pages/admin/SupportTicketDetails';
import DisputeDetails from './pages/admin/DisputeDetails';
import AdminProfile from './pages/admin/AdminProfile';
import OffersManagement from './pages/admin/OffersManagement';
import OfferDetails from './pages/admin/OfferDetails';
import CreateOffer from './pages/admin/CreateOffer';
import EditOffer from './pages/admin/EditOffer';
import CouponsManagement from './pages/admin/CouponsManagement';
import CouponDetails from './pages/admin/CouponDetails';
import CreateCoupon from './pages/admin/CreateCoupon';
import EditCoupon from './pages/admin/EditCoupon';
import SecurityAuditManagement from './pages/admin/SecurityAuditManagement';
import RolesPermissionsManagement from './pages/admin/RolesPermissionsManagement';
import FraudDetectionManagement from './pages/admin/FraudDetectionManagement';
import OTPSecurityManagement from './pages/admin/OTPSecurityManagement';
import NotificationManagement from './pages/admin/NotificationManagement';
import BusinessDocumentsManagement from './pages/admin/BusinessDocumentsManagement';
import TestimonialsManagement from './pages/admin/TestimonialsManagement';
import CreateTestimonial from './pages/admin/CreateTestimonial';
import EditTestimonial from './pages/admin/EditTestimonial';
import TestimonialDetails from './pages/admin/TestimonialDetails';
import BlogManagement from './pages/admin/BlogManagement';
import CreateBlog from './pages/admin/CreateBlog';
import EditBlog from './pages/admin/EditBlog';
import AdminBlogDetails from './pages/admin/BlogDetails';
import ContactSubmissionsManagement from './pages/admin/ContactSubmissionsManagement';
import ContactSubmissionDetails from './pages/admin/ContactSubmissionDetails';
import AdminBankAccounts from './pages/admin/AdminBankAccounts';
import WalletRequests from './pages/admin/WalletRequests';
import PaymentMethods from './pages/admin/PaymentMethods';
import JobsManagement from './pages/admin/JobsManagement';
import CreateJob from './pages/admin/CreateJob';
import EditJob from './pages/admin/EditJob';
import JobApplications from './pages/admin/JobApplications';
import MasterCatalogManagement from './pages/admin/MasterCatalogManagement';

// Super Admin Pages
import SuperAdminDashboard from './pages/superadmin/Dashboard';
import SuperAdminProfile from './pages/superadmin/SuperAdminProfile';
import SuperAdminAdminManagement from './pages/superadmin/AdminManagement';
import AddAdmin from './pages/superadmin/AddAdmin';
import EditAdmin from './pages/superadmin/EditAdmin';

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleGuard from './components/auth/RoleGuard';

// Styles
import 'react-toastify/dist/ReactToastify.css';

import { useDispatch } from 'react-redux';
import { restoreAuth } from './store/slices/authSlice';
import { IdleTimer } from './components/common/IdleTimer';
import { GlobalImagePreviewer } from './components/common/GlobalImagePreviewer';

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(restoreAuth());
  }, [dispatch]);

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <IdleTimer />
        <GlobalImagePreviewer />
        <Routes>
            {/* ============================================
                PUBLIC USER ROUTES (No Authentication Required) 
            ============================================ */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/features" element={<Features />} />
            <Route path="/how-it-works" element={<Features />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/qr-payment-solutions" element={<QRPaymentSolutions />} />
            <Route path="/services/business-discovery-platform" element={<BusinessDiscoveryPlatform />} />
            <Route path="/services/digital-wallet-system" element={<DigitalWalletSystem />} />
            <Route path="/services/analytics-reporting" element={<AnalyticsReporting />} />
            <Route path="/services/kyc-verification" element={<KYCVerification />} />
            <Route path="/services/mobile-applications" element={<MobileApplications />} />
            <Route path="/services/multi-platform-support" element={<MultiPlatformSupport />} />
            <Route path="/services/24-7-support" element={<Support247 />} />
            <Route path="/store" element={<Store />} />
            <Route path="/store/:id" element={<StoreDetails />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogDetails />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/app" element={<AppPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:id" element={<JobDetails />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/login" element={<LoginPortal />} />

            {/* ============================================
                ADMIN LOGIN & FORGOT PASSWORD (Public Access)
            ============================================ */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />

            {/* ============================================
                ADMIN PANEL ROUTES (Protected - Only Admin Role)
            ============================================ */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <RoleGuard requiredRole="admin">
                    <AdminLayout role="admin" />
                  </RoleGuard>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="businesses" element={<BusinessManagement />} />
              <Route path="businesses/:businessId" element={<BusinessDetails />} />
              <Route path="categories" element={<CategoryManagement />} />
              <Route path="master-catalog" element={<MasterCatalogManagement />} />
              <Route path="categories/add" element={<CategoryBuilder />} />
              <Route path="categories/edit/:categoryId" element={<CategoryBuilder />} />
              <Route path="dynamic-fields" element={<DynamicFieldsManagement />} />
              <Route path="field-groups" element={<FieldGroupsManagement />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="users/:userId" element={<UserDetails />} />
              <Route path="users/:userId/edit" element={<EditUser />} />
              <Route path="payments" element={<PaymentManagement />} />
              <Route path="wallet" element={<WalletSettlementManagement />} />
              <Route path="wallet-requests" element={<WalletRequests />} />
              <Route path="bank-accounts" element={<AdminBankAccounts />} />
              <Route path="payment-methods" element={<PaymentMethods />} />
              <Route path="settlements/:settlementId" element={<SettlementDetails />} />
              <Route path="analytics" element={<ReportsAnalytics />} />
              <Route path="reports" element={<ReportsAnalytics />} />
              <Route path="settings" element={<AppConfiguration />} />
              <Route path="support" element={<SupportDisputeManagement />} />
              <Route path="support/tickets/:ticketId" element={<SupportTicketDetails />} />
              <Route path="disputes/:disputeId" element={<DisputeDetails />} />
              <Route path="offers" element={<OffersManagement />} />
              <Route path="offers/create" element={<CreateOffer />} />
              <Route path="offers/:offerId" element={<OfferDetails />} />
              <Route path="offers/:offerId/edit" element={<EditOffer />} />
              <Route path="coupons" element={<CouponsManagement />} />
              <Route path="coupons/create" element={<CreateCoupon />} />
              <Route path="coupons/:couponId" element={<CouponDetails />} />
              <Route path="coupons/:couponId/edit" element={<EditCoupon />} />
              <Route path="security" element={<SecurityAuditManagement />} />
              <Route path="roles" element={<RolesPermissionsManagement />} />
              <Route path="fraud" element={<FraudDetectionManagement />} />
              <Route path="otp-security" element={<OTPSecurityManagement />} />
              <Route path="notifications" element={<NotificationManagement />} />
              <Route path="business-documents" element={<BusinessDocumentsManagement />} />
              <Route path="testimonials" element={<TestimonialsManagement />} />
              <Route path="testimonials/create" element={<CreateTestimonial />} />
              <Route path="testimonials/:id" element={<TestimonialDetails />} />
              <Route path="testimonials/:id/edit" element={<EditTestimonial />} />
              <Route path="blogs" element={<BlogManagement />} />
              <Route path="blogs/create" element={<CreateBlog />} />
              <Route path="blogs/:id" element={<AdminBlogDetails />} />
              <Route path="blogs/:id/edit" element={<EditBlog />} />
              <Route path="contact-submissions" element={<ContactSubmissionsManagement />} />
              <Route path="contact-submissions/:id" element={<ContactSubmissionDetails />} />
              <Route path="jobs" element={<JobsManagement />} />
              <Route path="jobs/create" element={<CreateJob />} />
              <Route path="jobs/:id/edit" element={<EditJob />} />
              <Route path="job-applications" element={<JobApplications />} />
              <Route path="profile" element={<AdminProfile />} />

            </Route>

            {/* ============================================
                SUPER ADMIN PANEL ROUTES (Protected - Only Super Admin Role)
            ============================================ */}
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <RoleGuard requiredRole="superadmin">
                    <SuperAdminLayout />
                  </RoleGuard>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
              <Route path="dashboard" element={<SuperAdminDashboard />} />
              <Route path="profile" element={<SuperAdminProfile />} />
              <Route path="admin-management" element={<SuperAdminAdminManagement />} />
              <Route path="admin-management/add" element={<AddAdmin />} />
              <Route path="admin-management/edit/:id" element={<EditAdmin />} />
            </Route>

            {/* ============================================
                404 - NOT FOUND
            ============================================ */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Toast Notifications */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1F2937',
                color: '#F9FAFB',
                border: '1px solid #374151',
              },
              success: {
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#F9FAFB',
                },
              },
              error: {
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#F9FAFB',
                },
              },
            }}
          />
        </div>
      </Router>
  );
}

export default App;
